// Race HUD: speed readout, nitro meter, lap/checkpoint progress, timer,
// position markers. Single source of truth wired from Game.
export class Hud {
  private readonly speedValue = this.getElement('#speed-value');
  private readonly speedBar = this.getElement('#speed-bar-fill');
  private readonly nitroFill = this.getElement('#nitro-fill');
  private readonly lapValue = this.getElement('#lap-value');
  private readonly lapTotal = this.getElement('#lap-total');
  private readonly gateValue = this.getElement('#gate-value');
  private readonly gateTotal = this.getElement('#gate-total');
  private readonly timerValue = this.getElement('#timer-value');
  private readonly bestValue = this.getElement('#best-value');
  private readonly statusLine = this.getElement('#status-line');
  private readonly banner = this.getElement('#event-banner');

  private lastLap = -1;

  setTotals(laps: number, gates: number): void {
    this.lapTotal.textContent = String(laps);
    this.gateTotal.textContent = String(gates);
  }

  setCountdown(value: number): void {
    const el = this.getElement('#countdown-value');
    if (value <= 0) {
      if (el.dataset.goShown === '1') return; // already fired
      el.dataset.goShown = '1';
      el.textContent = 'GO!';
      el.animate(
        [{ opacity: '1', transform: 'scale(1.4)' }, { opacity: '1', transform: 'scale(1.15)' }],
        { duration: 550, easing: 'ease-out' },
      ).onfinish = () => {
        el.animate(
          [{ opacity: '1' }, { opacity: '0' }],
          { duration: 400, easing: 'ease-out' },
        ).onfinish = () => { el.textContent = ''; };
      };
      return;
    }
    el.dataset.goShown = '0';
    const whole = Math.max(1, Math.min(3, Math.ceil(value)));
    if (el.textContent !== String(whole) && String(whole) !== 'NaN') {
      el.textContent = String(whole);
      el.animate(
        [{ opacity: '1', transform: 'scale(1.5)' }, { opacity: '1', transform: 'scale(1)' }],
        { duration: 400, easing: 'ease-out' },
      );
    }
  }

  setDrift(value: number): void {
    this.getElement('#drift-fill').style.width = `${Math.min(100, value * 100)}%`;
  }

  update(state: {
    position: number;
    speed: number;
    maxSpeed: number;
    nitro: number;
    lap: number;
    gate: number;
    gates: number;
    elapsed: number;
    finished: boolean;
  }): void {
    this.speedValue.textContent = String(Math.round(state.speed * 3.6));
    this.speedBar.style.width = `${Math.min(100, (state.speed / state.maxSpeed) * 100)}%`;
    this.nitroFill.style.width = `${Math.min(100, state.nitro * 100)}%`;
    this.lapValue.textContent = String(Math.max(1, state.lap));
    this.gateValue.textContent = String(state.gate + 1 > state.gates ? state.gates : state.gate + 1);
    this.timerValue.textContent = this.formatTime(state.elapsed);
    this.bestValue.textContent = this.formatTime(Hud.bestTime);
    const positionEl = this.getElement('#position-value');
    positionEl.textContent = String(state.position);
    this.statusLine.textContent = state.finished ? 'Race complete!' : 'Pass the gates';
    void this.lastLap;
  }

  bannerFlash(text: string, color: string): void {
    this.banner.textContent = text;
    this.banner.style.color = color;
    this.banner.animate(
      [
        { opacity: '0', transform: 'translate(-50%, -8px) scale(0.9)' },
        { opacity: '1', transform: 'translate(-50%, 0) scale(1.05)' },
        { opacity: '1', transform: 'translate(-50%, 0) scale(1)' },
        { opacity: '0', transform: 'translate(-50%, 10px) scale(0.95)' },
      ],
      { duration: 1400, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' },
    );
  }

  punchSpeed(): void {
    this.speedValue.animate(
      [{ transform: 'scale(1.25)' }, { transform: 'scale(1)' }],
      { duration: 140, easing: 'ease-out' },
    );
  }

  private static bestTime = 0;

  static setBestTime(seconds: number): void {
    if (Hud.bestTime === 0 || seconds < Hud.bestTime) Hud.bestTime = seconds;
  }

  private formatTime(seconds: number): string {
    if (seconds === 0) return '--:--.--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    const cs = Math.floor((seconds % 1) * 100);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
  }

  private getElement(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}
